import { config } from '../domain/config'

export interface GoogleCredentialResponse { credential?: string; select_by?: string }
export interface TeacherIdentity { email: string; name: string; sub: string }
export interface TeacherSession { idToken: string; teacher: TeacherIdentity; expiresAt: number }

interface GoogleIdConfiguration {
  client_id: string
  callback: (response: GoogleCredentialResponse) => void
  auto_select?: boolean
  cancel_on_tap_out?: boolean
  context?: 'signin' | 'signup' | 'use'
  ux_mode?: 'popup' | 'redirect'
}
interface GoogleButtonConfiguration {
  type?: 'standard' | 'icon'; theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'large' | 'medium' | 'small'; text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  shape?: 'rectangular' | 'pill' | 'circle' | 'square'; width?: number; logo_alignment?: 'left' | 'center'
}
export interface GoogleIdentityApi {
  accounts: { id: {
    initialize: (configuration: GoogleIdConfiguration) => void
    renderButton: (parent: HTMLElement, configuration: GoogleButtonConfiguration) => void
    disableAutoSelect: () => void
  } }
}
declare global { interface Window { google?: GoogleIdentityApi } }

interface CredentialClaims {
  aud: string; email: string; email_verified: boolean | string; exp: number
  iss: string; name?: string; sub: string
}

const GIS_SCRIPT_ID = 'google-identity-services'
const SESSION_KEY = 'hiltac-teacher-google-session'
const VALID_ISSUERS = new Set(['accounts.google.com', 'https://accounts.google.com'])

function decodeBase64Url(value: string) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
  const binary = atob(padded)
  return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)))
}

// This client-side check improves UX only. Apps Script remains the authorization authority.
export function parseCredentialClaims(idToken: string, expectedClientId: string, now = Date.now()): CredentialClaims {
  const parts = idToken.split('.')
  if (parts.length !== 3) throw new Error('AUTH_INVALID')
  let claims: CredentialClaims
  try { claims = JSON.parse(decodeBase64Url(parts[1])) as CredentialClaims }
  catch { throw new Error('AUTH_INVALID') }
  if (!VALID_ISSUERS.has(claims.iss)) throw new Error('AUTH_INVALID_ISSUER')
  if (!expectedClientId || claims.aud !== expectedClientId) throw new Error('AUTH_INVALID_AUDIENCE')
  if (claims.email_verified !== true && claims.email_verified !== 'true') throw new Error('EMAIL_NOT_VERIFIED')
  if (!claims.email || !claims.sub || !Number.isFinite(Number(claims.exp))) throw new Error('AUTH_INVALID')
  if (Number(claims.exp) * 1000 <= now) throw new Error('AUTH_EXPIRED')
  return claims
}

export function sessionIsUsable(session: TeacherSession | null, now = Date.now()) {
  return Boolean(session?.idToken && session.teacher.email && session.expiresAt > now + 30_000)
}
export function readTeacherSession(): TeacherSession | null {
  try {
    const value = sessionStorage.getItem(SESSION_KEY)
    if (!value) return null
    const session = JSON.parse(value) as TeacherSession
    if (!sessionIsUsable(session)) { sessionStorage.removeItem(SESSION_KEY); return null }
    return session
  } catch { sessionStorage.removeItem(SESSION_KEY); return null }
}
export function saveTeacherSession(idToken: string, teacher: TeacherIdentity): TeacherSession {
  const claims = parseCredentialClaims(idToken, config.googleClientId)
  const session = { idToken, teacher, expiresAt: Number(claims.exp) * 1000 }
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
  return session
}
export function clearTeacherSession() {
  sessionStorage.removeItem(SESSION_KEY)
  window.google?.accounts.id.disableAutoSelect()
}

export function authErrorMessage(reason: unknown) {
  const code = reason instanceof Error ? reason.message : String(reason)
  if (code.includes('CONFIG_GOOGLE_CLIENT_ID_REQUIRED')) return 'Falta GOOGLE_CLIENT_ID en las propiedades de Apps Script.'
  if (code.includes('TEACHER_NOT_ALLOWED')) return 'Tu cuenta Google no está autorizada para usar el panel profesor.'
  if (code.includes('AUTH_INVALID_AUDIENCE')) return 'La cuenta fue autenticada para otra aplicación. Revisa el Client ID configurado.'
  if (code.includes('EMAIL_NOT_VERIFIED')) return 'Google no ha verificado el correo de esta cuenta.'
  if (code.includes('AUTH_EXPIRED')) return 'La sesión de Google expiró. Inicia sesión nuevamente.'
  if (code.includes('AUTH_REQUIRED') || code.includes('AUTH_INVALID')) return 'No se pudo validar la sesión con Google.'
  return 'No se pudo iniciar sesión. Inténtalo nuevamente.'
}

export function loadGoogleIdentityServices(): Promise<GoogleIdentityApi> {
  if (window.google?.accounts.id) return Promise.resolve(window.google)
  return new Promise((resolve, reject) => {
    const finish = () => window.google?.accounts.id ? resolve(window.google) : reject(new Error('GIS_UNAVAILABLE'))
    const existing = document.getElementById(GIS_SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', finish, { once: true })
      existing.addEventListener('error', () => reject(new Error('GIS_UNAVAILABLE')), { once: true })
      return
    }
    const script = document.createElement('script')
    script.id = GIS_SCRIPT_ID; script.src = 'https://accounts.google.com/gsi/client?hl=es'; script.async = true; script.defer = true
    script.onload = finish; script.onerror = () => reject(new Error('GIS_UNAVAILABLE'))
    document.head.appendChild(script)
  })
}
